import { createSlice } from '@reduxjs/toolkit';
import { initialEditAssessmentState } from "./editAssessmentConstants";
import editAssessmentExtraReducers from "./editAssessmentExtraReducers";
import { toast } from "react-toastify";
import CONSTANT from "../../constants/Constant";
import { getAllVisibleQuestions } from "../../components/editAssessmentNew/EditAssessmentService";
import { makeSerializable } from "../../utils/Utils";
import { checkAssessmentBlockedState } from "./editAssessmentUtils";


const editAssessmentSlice = createSlice({
    name: 'editAssessment',
    initialState: initialEditAssessmentState,
    reducers: {
        setTestId: (state, action) => {
            state.baseData.testId = action.payload;
        },
        setCurrentGroupId: (state, action) => {
            const groupId = action.payload ? action.payload : CONSTANT.ASSESSMENT_GROUP.DEMOGRAPHICS.key;
            state.baseData.currentGroupId = groupId;
        },
        setCurrentSubGroupIndex: (state, action) => {
            const subGroupIndex = action.payload ? action.payload : 0;
            state.baseData.currentSubGroupIndex = subGroupIndex;
        },
        setShowInlineError: (state, action) => {
            state.baseData.showInlineError = action.payload;
        },
        setCurrentQuestionId: (state, action) => {
            state.baseData.currentQuestionId = action.payload;
        },
        setShowInstruction: (state, action) => {
            state.baseData.showInstruction = action.payload;
        },
        setOpenBiometricMovementAndCautionQuestionContainer: (state, action) => {
            state.baseData.openBiometricMovementAndCautionQuestionContainer = action.payload;
        },
        setGroupCompleted: (state, action) => {
            const { groupId, isCompleted } = action.payload;
            const { groups } = state;

            const group = groups.find(group => group.groupId === groupId);
            if(!group) {
                return;
            }
            group.groupCompleted = isCompleted;
        },
        setQuestionAnswer: (state, action) => {
            const { questionId, answerId, answerValue, subGroupIndex } = action.payload;

            const { groups, baseData } = state;
            const { currentGroupId } = baseData;
            const currentGroup = groups.find(group => group.groupId === currentGroupId);
            if (!currentGroup || !currentGroup.subGroups) {
                console.error('Group not found or subGroups missing');
                return;
            }
            const currentSubGroup = currentGroup.subGroups[subGroupIndex];
            if (!currentSubGroup) {
                console.error('SubGroup not found');
                return;
            }

            const question = currentSubGroup.questions.find(question => question.questionId === questionId);
            if(!question) {
                toast.error('Question not found');
                console.error('Question not found');
                return;
            }

            if((question?.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX) || (question?.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT)) {
                let tempAnswerList = question?.answerList ?? [];
                let doesAlreadySelectedCurrentAnswer = tempAnswerList.find(answer => answer === answerId);
                if(doesAlreadySelectedCurrentAnswer) {
                    tempAnswerList = tempAnswerList.filter(currentAnswerId => currentAnswerId !== answerId);
                } else {
                    tempAnswerList.push(answerId);
                }

                question.answerList = tempAnswerList;
            } else {
                question.answerId = answerId;
                question.answerValue = answerValue;
            }

            // Automatically re-calculate blocked status on every answer change
            const { isBlocked, exitWorkflowMessage } = checkAssessmentBlockedState(groups);
            state.baseData.isBlocked = isBlocked;
            state.baseData.exitWorkflowMessage = exitWorkflowMessage;
        },
        handleHideOrShowQuestion: (state, action) => {
            const { questionId, isDefaultHide } = action.payload;
            const { groups } = state;

            let tempQuestion = null;
            groups.forEach(group => {
            if(group.subGroups.length > 0) {
                group.subGroups.map(subGroup => {
                    if(subGroup.questions.length > 0) {
                        subGroup.questions.map(question => {
                            if(question.questionId === questionId) {
                                tempQuestion = question;
                            }
                        })
                    }
                })
            }
            })
            if(!tempQuestion) {
                // toast.error('Question not found');
                console.error('Question not found');
                return;
            }
            tempQuestion.isDefaultHide = isDefaultHide;
            if(isDefaultHide) {
                tempQuestion.answerId = 0;
                tempQuestion.answerValue = "";
            }

            // Automatically re-calculate blocked status on visibility change
            const { isBlocked, exitWorkflowMessage } = checkAssessmentBlockedState(groups);
            state.baseData.isBlocked = isBlocked;
            state.baseData.exitWorkflowMessage = exitWorkflowMessage;
        },
        setAllQuestionsError: (state, action) => {
            const { groups, baseData } = state;
            const questionsWithValidations = action.payload?.questionsWithValidations || [];
            const currentGroupId = action.payload?.currentGroupId >= 0 ? action.payload.currentGroupId : baseData?.currentGroupId;
            const currentSubGroupIndex = action.payload?.currentSubGroupIndex >= 0 ? action.payload.currentSubGroupIndex : baseData?.currentSubGroupIndex;

            const currentGroup = groups.find(group => group.groupId === currentGroupId);
            if (!currentGroup || !currentGroup.subGroups) {
                return;
            }
            const lengthOfSubGroup = currentGroup.subGroups.length;
            //CC do not have any subgroup
            if(lengthOfSubGroup === 0) {
                return;
            }

            const questions = getAllVisibleQuestions(currentGroup, currentSubGroupIndex);

            questionsWithValidations.forEach((questionWithValidation) => {
                const question = questions.find(question => question.questionId === questionWithValidation.questionId);
                if(!question) {
                    toast.error('Question not found');
                    console.error('Question not found');
                    return;
                }
                question.error = questionWithValidation.error;
            })
        },
        clearEditAssessmentData: (state) => {
            return initialEditAssessmentState;
        },
        updateCCQuestionAnswer: (state, action) => {
            const { answerId, answerValue } = action.payload;
            const { ccQuestion } = state;
            ccQuestion.answerId = answerId;
            ccQuestion.answerValue = answerValue;
        },
        resetAnswerOfCurrentQuestion: (state, action) => {
            const { questionId } = action.payload;
            const { groups, baseData } = state;
            const { currentGroupId, currentSubGroupIndex } = baseData;
            const currentGroup = groups.find(group => group.groupId === currentGroupId);
            if (!currentGroup || !currentGroup.subGroups) {
                return;
            }
            const currentSubGroup = currentGroup.subGroups[currentSubGroupIndex];
            if (!currentSubGroup) {
                return;
            }
            const question = currentSubGroup.questions.find(question => question.questionId === questionId);
            if(!question) {
                toast.error('Question not found');
                console.error('Question not found');
                return;
            }

            if((question?.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX) || (question?.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT)) {
                question.answerList = [];
            } else {
                question.answerId = 0;
                question.answerValue = "";
            }

            // Automatically re-calculate blocked status on reset
            const { isBlocked, exitWorkflowMessage } = checkAssessmentBlockedState(groups);
            state.baseData.isBlocked = isBlocked;
            state.baseData.exitWorkflowMessage = exitWorkflowMessage;
        },
        setEditAssessmentBlocked: (state, action) => {
            state.baseData.isBlocked = true;
            state.baseData.exitWorkflowMessage = action.payload;
        },
        setEditAssessmentUnblocked: (state) => {
            state.baseData.isBlocked = false;
            state.baseData.exitWorkflowMessage = "";
        }
    },
    extraReducers: editAssessmentExtraReducers,
});

export const {
    setTestId,
    setOpenBiometricMovementAndCautionQuestionContainer,
    setCurrentGroupId,
    setCurrentSubGroupIndex,
    setQuestionAnswer,
    setShowInlineError,
    setAllQuestionsError,
    setCurrentQuestionId,
    clearEditAssessmentData,
    updateCCQuestionAnswer,
    setShowInstruction,
    setGroupCompleted,
    handleHideOrShowQuestion,
    resetAnswerOfCurrentQuestion,
    setEditAssessmentBlocked,
    setEditAssessmentUnblocked
} = editAssessmentSlice.actions;

export default editAssessmentSlice.reducer;
